package system

import abilities.*
import b2d.AbilityObjectFactory.buildFireBolt
import com.github.quillraven.fleks.*
import com.github.quillraven.fleks.World.Companion.family
import com.github.quillraven.fleks.World.Companion.inject
import com.soywiz.korge.box2d.*
import com.soywiz.korge.view.*
import component.*
import component.ability.*
import input.*
import org.jbox2d.common.*
import utils.*

class PlayerControlSystem(
    private val inputController: InputController = inject(),
    private val camera: Camera = inject(),
    private val physicWorld: org.jbox2d.dynamics.World = inject(),
) : IteratingSystem(family { all(PlayerComponent) }) {

    companion object {
        const val JUMP_IMPULSE = -2.5f
        const val WALL_JUMP_IMPULSE = -2.5f
        const val WALL_JUMP_DELAY = 1f / 60f * 8f
        const val LINEAR_VELOCITY_UPPER_BOUND_GROUNDED_X = 2.5f
        const val LINEAR_VELOCITY_LOWER_BOUND_GROUNDED_X = -2.5f

        const val LINEAR_VELOCITY_UPPER_BOUND_JUMPING_X = 1.5f
        const val LINEAR_VELOCITY_LOWER_BOUND_JUMPING_X = -1.5f
    }

    private var wallDelayAccumulator = 0f

    override fun onTickEntity(entity: Entity) {
        val playerComp = entity[PlayerComponent]
        val physComp = entity[PhysicComponent]
        val stateComp = entity[StateComponent]

        if (inputController.left.current) {
            when (stateComp.state) {
                StateComponent.STATE_NORMAL, StateComponent.STATE_MOVING -> {
                    if (physComp.body.linearVelocityX >= LINEAR_VELOCITY_LOWER_BOUND_GROUNDED_X) {
                        physComp.impulse.x = -0.4f
                    }
                }

                StateComponent.STATE_JUMPING, StateComponent.STATE_FALLING -> {
                    if (physComp.body.linearVelocityX >= LINEAR_VELOCITY_LOWER_BOUND_JUMPING_X) {
                        physComp.impulse.x = -0.15f
                    }
                }
            }

            playerComp.direction = BodyDirection.LEFT
        }

        if (inputController.right.current) {
            when (stateComp.state) {
                StateComponent.STATE_NORMAL, StateComponent.STATE_MOVING -> {
                    if (physComp.body.linearVelocityX <= LINEAR_VELOCITY_UPPER_BOUND_GROUNDED_X) {
                        physComp.impulse.x = 0.4f
                    }
                }

                StateComponent.STATE_JUMPING, StateComponent.STATE_FALLING -> {
                    if (physComp.body.linearVelocityX <= LINEAR_VELOCITY_UPPER_BOUND_JUMPING_X) {
                        physComp.impulse.x = 0.15f
                    }
                }
            }

            playerComp.direction = BodyDirection.RIGHT
        }

        if (inputController.jump.current) {
            when (stateComp.state) {
                // jump
                StateComponent.STATE_NORMAL, StateComponent.STATE_MOVING -> {
                    physComp.impulse.y = JUMP_IMPULSE
                    stateComp.state = StateComponent.STATE_JUMPING
                }
                // wall jump
                StateComponent.STATE_ON_WALL -> {
//                    val isOnWall = playerComp.playerMetadata.playerPhysicState.isOnWall
//                    inputController.jump.current = false
                    if (wallDelayAccumulator < WALL_JUMP_DELAY) {
                        wallDelayAccumulator += deltaTime
                    } else {
                        wallDelayAccumulator = 0f
                        // перед импульсом обнуляем скорость игрока
                        physComp.body.linearVelocityX = 0f
                        physComp.body.linearVelocityY = 0f

                        physComp.impulse.y = WALL_JUMP_IMPULSE
                        physComp.impulse.x = if (playerComp.playerMetadata.playerPhysicState.isOnRightWall) {
                            playerComp.direction = BodyDirection.RIGHT
                            3f
                        } else {
                            playerComp.direction = BodyDirection.LEFT
                            -3f
                        }
                        stateComp.state = StateComponent.STATE_JUMPING
                        inputController.jump.current = false
                    }
                }
            }
        }

        physComp.body.applyLinearImpulse(physComp.impulse, physComp.body.worldCenter, true)
        physComp.impulse.setZero()

        if (inputController.firstAbility.current &&
            playerComp.abilitySelector.isFirstSelected &&
            playerComp.abilitySelector.firstAbilityCooldowner.isReady
        ) {
            println("FirstAbility tap: ${playerComp.abilitySelector.firstAbility}")
            applyAbility(playerComp.abilitySelector.firstAbility, playerComp, physComp)
            playerComp.abilitySelector.firstAbilityCooldowner.useAbility()
        }

        if (inputController.secondAbility.current &&
            playerComp.abilitySelector.isSecondSelected &&
            playerComp.abilitySelector.secondAbilityCooldowner.isReady
        ) {
            println("SecondAbility tap: ${playerComp.abilitySelector.secondAbility}")
            applyAbility(playerComp.abilitySelector.secondAbility, playerComp, physComp)
            playerComp.abilitySelector.secondAbilityCooldowner.useAbility()
        }
    }

    private fun applyAbility(ability: Abilities, playerComp: PlayerComponent, playerPhysicComp: PhysicComponent) {
        when (ability) {
            Abilities.FIRE_BOLT -> {
                applyFireBolt(playerComp, playerPhysicComp)
            }

            else -> {}
        }
    }

    private val tempAbilityPos = Vec2()
    private fun applyFireBolt(playerComp: PlayerComponent, playerPhysicComp: PhysicComponent) {
        if (playerComp.direction == BodyDirection.RIGHT) {
            tempAbilityPos.set(
                playerPhysicComp.body.position.x * physicWorld.customScale + PlayerBuilder.PLAYER_RADIUS * 2,
                playerPhysicComp.body.position.y * physicWorld.customScale
            )
        } else {
            tempAbilityPos.set(
                playerPhysicComp.body.position.x * physicWorld.customScale - PlayerBuilder.PLAYER_RADIUS * 2,
                playerPhysicComp.body.position.y * physicWorld.customScale
            )
        }

        println(
            """
            firebolt: ${tempAbilityPos}
        """.trimIndent()
        )

        val fireBolt = FireBolt()

        val view = camera.buildFireBolt(
            tempAbilityPos.x,
            tempAbilityPos.y,
            Sprite(),
            fireBolt,
            physicWorld
        )

        world.entity {
            it += PhysicComponent(
                body = view.body!!,
                vXmax = 5f,
                vYmax = 5f
            )

            it += AbilityComponent(
                ability = Abilities.FIRE_BOLT,
                body = view.body!!,
                view = view,
                direction = if (playerComp.direction == BodyDirection.RIGHT) {
                    Vec2(4f, 0f)
                } else {
                    Vec2(-4f, 0f)
                },
                fireBolt = fireBolt
            )
        }

        tempAbilityPos.setZero()
    }
}
